import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Competitor } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  ConfidenceBadge,
  Delta,
  Empty,
  ErrorBox,
  Loading,
  LoadingDashboard,
  PageHeader,
  Paywall,
  Why,
} from "../components/ui";
import { IconPlus, IconTarget } from "../components/icons";
import { date, dateTime, num, plural, stateWord } from "../lib/format";

interface ListResponse {
  tracked: Competitor[];
  candidates: Competitor[];
  discovery_signals: Record<string, string>;
}

interface DigestResponse {
  period_start: string;
  competitors: {
    competitor_id: number;
    title: string;
    subscribers: number | null;
    changes: {
      kind: string;
      title: string;
      value: number | null;
      previous: number | null;
      delta_pct: number | null;
      sample_size: number;
      confidence: string;
      evidence: Record<string, unknown>;
    }[];
  }[];
}

interface EventsResponse {
  events: {
    key: string;
    title: string;
    first_at: string;
    first_by: string;
    status: string;
    ours_delay_minutes: number | null;
    competitors_count: number;
    members: { side: string; name: string; at: string; views: number | null }[];
  }[];
  summary: { covered: number; missed: number; unique: number };
}

const KIND_TITLE: Record<string, string> = {
  growth: "Рост",
  cadence: "Частота",
  format_mix: "Форматы",
  topic_mix: "Темы",
  breakout: "Выстрелившая публикация",
  ads: "Реклама",
};

export default function CompetitorsPage() {
  const { channelId, can } = useSession();
  const queryClient = useQueryClient();
  const [identifier, setIdentifier] = useState("");

  const list = useQuery({
    queryKey: ["competitors", channelId],
    queryFn: () => api.get<ListResponse>(`/competitors/${channelId}`),
    enabled: Boolean(channelId),
  });
  const digest = useQuery({
    queryKey: ["competitor-digest", channelId],
    queryFn: () => api.get<DigestResponse>(`/competitors/${channelId}/digest`),
    enabled: Boolean(channelId),
  });
  const events = useQuery({
    queryKey: ["competitor-events", channelId],
    queryFn: () => api.get<EventsResponse>(`/competitors/${channelId}/events`),
    enabled: Boolean(channelId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["competitors", channelId] });
    queryClient.invalidateQueries({ queryKey: ["competitor-digest", channelId] });
  };

  const discover = useMutation({
    mutationFn: () => api.post(`/competitors/${channelId}/discover`),
    onSuccess: invalidate,
  });
  const add = useMutation({
    mutationFn: () => api.post(`/competitors/${channelId}`, { identifier }),
    onSuccess: () => {
      setIdentifier("");
      invalidate();
    },
  });
  const confirm = useMutation({
    mutationFn: (id: number) => api.post(`/competitors/${channelId}/${id}/confirm`),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (id: number) => api.post(`/competitors/${channelId}/${id}/reject`),
    onSuccess: invalidate,
  });
  const sync = useMutation({
    mutationFn: (id: number) => api.post(`/competitors/${channelId}/${id}/sync`),
    onSuccess: invalidate,
  });

  if (!channelId) return <Empty title="Сначала подключите канал" />;
  if (list.error instanceof ApiError && list.error.isPaywall) return <Paywall what="Слежение за конкурентами" />;
  if (list.isLoading) return <LoadingDashboard />;
  if (list.error) return <ErrorBox error={list.error} />;
  const data = list.data!;

  return (
    <>
      <PageHeader
        eyebrow="Детальная аналитика"
        title="Конкуренты"
        lede="Собирается только то, что видит обычный читатель: время выхода, просмотры, реакции, пересылки и число подписчиков. Прав администратора в чужом канале нам никто не даёт, и источники чужого роста нам не видны."
      />

      {can("write_competitors") && (
        <Card title="Добавить канал" note="по адресу — или предложим сами по вашим же данным">
          <div className="row">
            <input
              placeholder="@имя канала или ссылка"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              style={{ maxWidth: 320 }}
            />
            <button className="primary" onClick={() => add.mutate()} disabled={!identifier || add.isPending}>
              <IconPlus size={15} />
              Добавить
            </button>
            <button onClick={() => discover.mutate()} disabled={discover.isPending}>
              {discover.isPending ? "Ищем…" : "Найти самому"}
            </button>
          </div>
          {add.error && <ErrorBox error={add.error} />}
        </Card>
      )}

      {data.candidates.length > 0 && (
        <Card
          title="Кандидаты"
          note="найдены по вашим же данным — подтвердите, чтобы начать собирать"
        >
          {data.candidates.map((candidate) => (
            <div key={candidate.id} className="signal info">
              <span className="rail" />
              <div className="signal-body">
                <div className="what">@{candidate.identifier}</div>
                <div className="check">
                  {data.discovery_signals[candidate.discovered_by] ?? candidate.discovered_by}
                  {candidate.discovery_score !== null && ` · вес ${num(candidate.discovery_score)}`}
                </div>
                <Why summary="На чём основано">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {Object.entries(candidate.discovery_evidence ?? {}).flatMap(([signal, notes]) =>
                      (notes as string[]).slice(0, 3).map((note, index) => (
                        <li key={`${signal}-${index}`}>{note}</li>
                      )),
                    )}
                  </ul>
                </Why>
                {can("write_competitors") && (
                  <div className="row" style={{ marginTop: 10 }}>
                    <button className="primary small" onClick={() => confirm.mutate(candidate.id)}>
                      Следить
                    </button>
                    <button className="small" onClick={() => reject.mutate(candidate.id)}>
                      Не интересно
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      <Card
        title="За кем следим"
        note={`${num(data.tracked.length)} ${plural(data.tracked.length, "канал", "канала", "каналов")}`}
      >
        {data.tracked.length === 0 ? (
          <Empty title="Пока никого" icon={<IconTarget size={22} />}>
            Нажмите «Найти самому» — система предложит кандидатов по упоминаниям в вашей ленте,
            общим источникам и рекламным ссылкам.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Канал</th>
                  <th className="num">Подписчиков</th>
                  <th className="num">Собрано публикаций</th>
                  <th>Состояние</th>
                  <th>Обновлено</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.tracked.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title ?? `@${item.identifier}`}</td>
                    <td className="num mono">{num(item.subscribers)}</td>
                    <td className="num mono">{num(item.posts_collected)}</td>
                    <td>
                      <Badge tone={item.status === "ok" ? "ok" : item.status === "error" ? "alert" : "muted"}>
                        {stateWord(item.status)}
                      </Badge>
                      {item.last_error && <div className="faint">{item.last_error}</div>}
                    </td>
                    <td className="dim">{item.last_sync_at ? dateTime(item.last_sync_at) : "—"}</td>
                    <td>
                      {can("write_competitors") && (
                        <button className="small" onClick={() => sync.mutate(item.id)} disabled={sync.isPending}>
                          Обновить
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Что изменилось за месяц"
        note={digest.data ? `неделя от ${date(digest.data.period_start)}` : undefined}
      >
        {digest.isLoading ? (
          <Loading rows={3} />
        ) : !digest.data?.competitors.length ? (
          <p className="muted">
            Сводка появится после первого недельного пересчёта — или нажмите «Обновить»
            у конкурента.
          </p>
        ) : (
          digest.data.competitors.map((entry) => (
            <div key={entry.competitor_id} style={{ marginBottom: 16 }}>
              <h3>
                {entry.title} <span className="faint">· {num(entry.subscribers)} подписчиков</span>
              </h3>
              <div className="table-wrap">
                <table>
                  <tbody>
                    {entry.changes.map((change, index) => (
                      <tr key={`${change.kind}-${index}`}>
                        <td style={{ width: 170 }} className="dim">
                          {KIND_TITLE[change.kind] ?? change.kind}
                        </td>
                        <td>{change.title}</td>
                        <td style={{ width: 120 }} className="num">
                          {change.delta_pct === null ? "" : <Delta value={change.delta_pct} />}
                        </td>
                        <td style={{ width: 170 }}>
                          <ConfidenceBadge level={change.confidence} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </Card>

      <Card
        title="Инфоповоды"
        note={
          events.data
            ? `писали и мы, и они: ${events.data.summary.covered} · только они: ${events.data.summary.missed} · только мы: ${events.data.summary.unique}`
            : undefined
        }
      >
        {events.isLoading ? (
          <Loading rows={3} />
        ) : !events.data?.events.length ? (
          <p className="muted">Общих поводов не нашлось — или конкуренты ещё не собраны.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Повод</th>
                  <th>Первым</th>
                  <th className="num">Каналов</th>
                  <th>Мы</th>
                </tr>
              </thead>
              <tbody>
                {events.data.events.slice(0, 20).map((event) => (
                  <tr key={event.key}>
                    <td>{event.title}</td>
                    <td className="dim">
                      {event.first_by === "ours" ? "мы" : "конкурент"} · {dateTime(event.first_at)}
                    </td>
                    <td className="num">{event.competitors_count}</td>
                    <td>
                      {event.status === "missed" ? (
                        <Badge tone="warn">пропустили</Badge>
                      ) : event.status === "unique" ? (
                        <Badge tone="accent">только мы</Badge>
                      ) : (
                        <Badge tone="ok">
                          {event.ours_delay_minutes === null
                            ? "писали"
                            : `отстали на ${Math.round(event.ours_delay_minutes / 60)} ч`}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="faint" style={{ marginTop: 10 }}>
          Склейка по пересечению ключевых слов: близкие по словам, но разные по сути
          публикации иногда попадают в один повод.
        </p>
      </Card>
    </>
  );
}
